// Funnel permissions step (ENG-3796).
//
// The funnel's remaining screens all depend on an OS answer the user has not
// been asked for yet: `network` gates discovery, `camera` and `microphone` gate
// the preview on magic-moment. Asking here, between the Bridge explainer and
// the detection wait, is the last point where the ask still has a reason
// attached — "so we can find your cameras" — rather than arriving cold.
//
// It reuses RawPermissionsModal rather than re-rendering the permission rows:
// that component owns the OS request calls, the per-permission continue rules,
// the skip affordance and the lifecycle telemetry. A second implementation of
// that would be a second thing to keep correct, and the two would drift.
// Per rule: investigate-before-adding (ai.knowledge/global).
//
// The gate claims the ask for the whole time the funnel is open (see
// gate.tsx), so the standalone OnboardPermissionsModal on the room route stands
// down and cannot open underneath this one.
//
// Per rules: code-comments-required, reference-rules-in-comments
// (ai.knowledge/global).

import * as Dialog from '@studio/design-system/dialog';
import { type ReactElement, useEffect, useRef } from 'react';
import { createPermissionsModalInstanceId } from '#~/analytics/domains/index.js';
import { useConfig } from '#~/config/hooks.js';
import {
  RawPermissionsModal,
  PermissionsOSRecovery
} from '#~/routes/electron/permissions/permissions.js';
import { usePermissions } from '#~/routes/electron/permissions/use-permissions.js';
import { useInitialPermissionsCheck } from '#~/routes/electron/permissions/prompt-gate.js';
import { funnelOwnsPermissions } from '../gate-decisions.js';

interface ScreenPermissionsProps {
  /** Permissions answered (granted or explicitly skipped): go to detection. */
  readonly onNext: () => void;
}

export const ScreenPermissions = ({ onNext }: ScreenPermissionsProps): ReactElement => {
  // Deliberately no useStepView: RawPermissionsModal already emits its own
  // lifecycle events (permissions_modal_opened / _continue / _closed) carrying
  // the `surface` below, so this screen is measurable without adding a new
  // position to the shared onboarding taxonomy. Adding one there would change
  // the canonical event map and the funnel's step denominators for a screen the
  // taxonomy has never had.
  const config = useConfig();
  const {
    isOSKnown,
    osError,
    isOSFetching,
    retryOS,
    permissions: { camera, microphone, network }
  } = usePermissions();

  // Only the three the funnel actually depends on. `startup` is a preference,
  // not a prerequisite — a user who declines it still finds cameras.
  const core = [camera, microphone, network].filter((µ) => µ.enabled);
  const needed = funnelOwnsPermissions({
    sandboxDisabled: config.noMediaInputSandbox === true,
    loaded: core.every((µ) => !µ.isLoading),
    allowed: core.every((µ) => µ.permissionState === 'allowed')
  });
  // Distinguish "granted already" from "still loading": both report `needed`
  // false, but only the first should skip the step. Forwarding while the OS
  // query is in flight would race past an ask the user does need.
  const settled =
    config.noMediaInputSandbox === true || (isOSKnown && core.every((µ) => !µ.isLoading));

  const { open, close } = Dialog.useDialog(RawPermissionsModal);
  // One open per mount. The effect's deps include `open`, which Dialog.useDialog
  // may return fresh each render, so without this the dialog would reopen on
  // every re-render and stamp a new modal instance id each time.
  const openedRef = useRef(false);

  useEffect(() => {
    if (openedRef.current) return;
    const complete = () => {
      // A completed permission decision lets setup advance without treating a media denial as a grant.
      useInitialPermissionsCheck.getState().finishPrompt();
      close();
      onNext();
    };
    // Nothing to ask for: hand straight to detection. This is the common path
    // for a user who granted permissions on an earlier launch and restarted the
    // funnel from Settings, and it is why the gate can route through this step
    // unconditionally.
    if (settled && !needed) {
      openedRef.current = true;
      complete();
      return;
    }
    if (!settled) return;

    openedRef.current = true;

    open?.({
      title: "Let's get your Studio set up",
      description: 'Iris needs these to find cameras on your network',
      // Both paths advance. A user who declines still reaches detection — the
      // funnel reports what happened, it does not trap them on this screen.
      onContinue: complete,
      onSkip: complete,
      modalInstanceId: createPermissionsModalInstanceId(),
      surface: 'onboarding_funnel_permissions'
    });
  }, [close, needed, onNext, open, settled]);

  // Remove the owned dialog on unmount. Its permission controls govern completion;
  // the shared dialog disables Escape and outside dismissal.
  useEffect(() => {
    return () => {
      close();
    };
  }, [close]);

  // Keep the permission decision pending on lookup errors, but make its recovery reachable.
  return config.noMediaInputSandbox !== true && osError ? (
    <PermissionsOSRecovery
      retrying={isOSFetching}
      onRetry={() => {
        void retryOS();
      }}
    />
  ) : (
    <></>
  );
};
